import { useAuthentication, useAuthorization } from '@autone/auth';
import {
  useLazyGetCoreAcademyRedirectUriQuery,
  useLazyGetCoreDocumentationRedirectUrlQuery,
} from '@autone/redux/core';
import { CORE_ADMIN } from '@autone/utils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useMemo } from 'react';

import {
  DocumentationIcon,
  GraduationCapIcon,
  LockIcon,
  LogoutIcon,
  UserIcon,
  UsersIcon,
} from '../../../../icons';
import { HUB_PATH_APP } from '../../routes';
import { type UserCardConfig } from '../use-app-sidebar/useAppSidebar.types';
import { useWithClientRouting } from '../useWithClientRouting';

export const useSidebarProfile = () => {
  const withRouting = useWithClientRouting();

  const [academyTrigger, { isLoading: isAcademyLoading }] =
    useLazyGetCoreAcademyRedirectUriQuery();
  const [documentationTrigger, { isLoading: isDocumentationLoading }] =
    useLazyGetCoreDocumentationRedirectUrlQuery();

  const { signOut } = useAuthentication();

  const handleAcademyClick = useCallback(async () => {
    const data = await academyTrigger({}).unwrap();
    window.open(data.redirect_url);
  }, [academyTrigger]);

  const handleDocumentationClick = useCallback(async () => {
    const data = await documentationTrigger({}).unwrap();
    window.open(data.redirect_url);
  }, [documentationTrigger]);

  const { isAuthorized } = useAuthorization();

  const { showAcademy } = useFlags();

  const academyCardConfig: readonly UserCardConfig[] = useMemo(
    () =>
      showAcademy
        ? [
            {
              titleKey: 'sidenav.sections.settings.items.academy.name',
              icon: GraduationCapIcon,
              onClick: handleAcademyClick,
              isDisabled: isAcademyLoading,
            },
          ]
        : [],
    [showAcademy, handleAcademyClick, isAcademyLoading],
  );

  const userCardConfig: readonly UserCardConfig[] = useMemo(
    () => [
      ...academyCardConfig,
      {
        titleKey: 'sidenav.sections.settings.items.documentation.name',
        icon: DocumentationIcon,
        onClick: handleDocumentationClick,
        isDisabled: isDocumentationLoading,
      },
      {
        titleKey: 'sidenav.sections.settings.items.security.name',
        icon: LockIcon,
        ...withRouting(`${HUB_PATH_APP.settings}?tab=security`),
      },
      {
        titleKey: 'sidenav.sections.settings.items.profile.name',
        icon: UserIcon,
        ...withRouting(`${HUB_PATH_APP.settings}?tab=profile`),
      },
      {
        titleKey: 'sidenav.sections.account.items.team.name',
        icon: UsersIcon,
        url: HUB_PATH_APP.userManagement,
        isDisabled: !isAuthorized([CORE_ADMIN]),
      },
      {
        titleKey: 'sidenav.sections.settings.items.logout.name',
        icon: LogoutIcon,
        onClick: signOut,
      },
    ],
    [
      academyCardConfig,
      handleDocumentationClick,
      isDocumentationLoading,
      withRouting,
      signOut,
      isAuthorized,
    ],
  );

  return userCardConfig;
};
